import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Printer, RefreshCw } from "lucide-react";
import Receipt from "@/components/Receipt";

const fmt = (n, dp = 2) =>
  Number.isFinite(+n) ? Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }) : "—";

export default function NewTransaction() {
  const { t } = useApp();
  const [rate, setRate] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("walkin");
  const [customerName, setCustomerName] = useState("Walk-in");
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState("chain"); // chain = SAR->IDR(->USDT), direct = USDT->SAR, direct_usdt = SAR->USDT

  // Chain inputs
  const [sar, setSar] = useState("");
  const [leg1Rate, setLeg1Rate] = useState("");
  const [leg2Rate, setLeg2Rate] = useState("");
  const [convertToUsdt, setConvertToUsdt] = useState(true);
  const [override1, setOverride1] = useState(false);
  const [override2, setOverride2] = useState(false);

  // Direct USDT -> SAR inputs
  const [usdtIn, setUsdtIn] = useState("");
  const [leg3Rate, setLeg3Rate] = useState("");
  const [override3, setOverride3] = useState(false);

  // Direct SAR -> USDT inputs
  const [sarIn4, setSarIn4] = useState("");
  const [leg4Rate, setLeg4Rate] = useState("");
  const [override4, setOverride4] = useState(false);

  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadData = async () => {
    try {
      const [r, cs] = await Promise.all([api.get("/rates/current"), api.get("/customers")]);
      setRate(r.data);
      setCustomers(cs.data);
      if (r.data) {
        setLeg1Rate(r.data.sar_to_idr_sell ?? "");
        setLeg2Rate(r.data.idr_to_usdt_sell ?? "");
        setLeg3Rate(r.data.usdt_to_sar_sell ?? "");
        setLeg4Rate(r.data.sar_to_usdt_sell ?? "");
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadData(); }, []);

  // Chain calculations
  const idr = useMemo(() => (+sar || 0) * (+leg1Rate || 0), [sar, leg1Rate]);
  const usdt = useMemo(() => (+leg2Rate ? idr / +leg2Rate : 0), [idr, leg2Rate]);

  // Margin vs configured BUY rate (spread captured by desk), in IDR
  const leg1Margin = useMemo(() => {
    if (!rate?.sar_to_idr_buy || !leg1Rate) return 0;
    return (+sar || 0) * (+leg1Rate - rate.sar_to_idr_buy);
  }, [sar, leg1Rate, rate]);

  const leg2Margin = useMemo(() => {
    if (!rate?.idr_to_usdt_buy || !leg2Rate || !convertToUsdt) return 0;
    return usdt * (+leg2Rate - rate.idr_to_usdt_buy);
  }, [usdt, leg2Rate, rate, convertToUsdt]);

  // Direct USDT -> SAR calculations (margin expressed in IDR via IDR/USDT sell rate)
  const sarOut = useMemo(() => (+usdtIn || 0) * (+leg3Rate || 0), [usdtIn, leg3Rate]);
  const leg3Margin = useMemo(() => {
    if (!rate?.usdt_to_sar_buy || !leg3Rate) return 0;
    const spreadSar = (+usdtIn || 0) * (+leg3Rate - rate.usdt_to_sar_buy);
    return spreadSar * (rate?.idr_to_usdt_sell || 0);
  }, [usdtIn, leg3Rate, rate]);

  // Direct SAR -> USDT calculations (margin expressed in IDR via IDR/USDT sell rate)
  const usdtOut4 = useMemo(() => (+sarIn4 || 0) * (+leg4Rate || 0), [sarIn4, leg4Rate]);
  const leg4Margin = useMemo(() => {
    if (!rate?.sar_to_usdt_buy || !leg4Rate) return 0;
    const spreadUsdt = (+sarIn4 || 0) * (+leg4Rate - rate.sar_to_usdt_buy);
    return spreadUsdt * (rate?.idr_to_usdt_sell || 0);
  }, [sarIn4, leg4Rate, rate]);

  const totalProfit = mode === "direct" ? leg3Margin : mode === "direct_usdt" ? leg4Margin : leg1Margin + leg2Margin;

  const submit = async () => {
    let legs = [];
    if (mode === "direct") {
      if (!usdtIn || !leg3Rate) return toast.error("Enter USDT amount and rate");
      legs = [{ from_currency: "USDT", to_currency: "SAR", from_amount: +usdtIn, rate: +leg3Rate, to_amount: sarOut, market_rate: rate?.usdt_to_sar_buy ?? null, margin: leg3Margin }];
    } else if (mode === "direct_usdt") {
      if (!sarIn4 || !leg4Rate) return toast.error("Enter SAR amount and rate");
      legs = [{ from_currency: "SAR", to_currency: "USDT", from_amount: +sarIn4, rate: +leg4Rate, to_amount: usdtOut4, market_rate: rate?.sar_to_usdt_buy ?? null, margin: leg4Margin }];
    } else {
      if (!sar || !leg1Rate) return toast.error("Enter SAR amount and rate");
      legs = [{ from_currency: "SAR", to_currency: "IDR", from_amount: +sar, rate: +leg1Rate, to_amount: idr, market_rate: rate?.sar_to_idr_buy ?? null, margin: leg1Margin }];
      if (convertToUsdt) {
        legs.push({ from_currency: "IDR", to_currency: "USDT", from_amount: idr, rate: 1 / +leg2Rate, to_amount: usdt, market_rate: rate?.idr_to_usdt_buy ?? null, margin: leg2Margin });
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/transactions", {
        customer_id: customerId === "walkin" ? null : customerId,
        customer_name: customerName,
        legs,
        total_profit_idr: totalProfit,
        notes,
        status: "completed",
      });
      setSaved(data);
      toast.success(t("tx_created"), { description: t("tx_saved_desc") });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setSar(""); setUsdtIn(""); setSarIn4(""); setNotes(""); setSaved(null);
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t("new_transaction")}</h1>
        <p className="text-sm text-slate-400 mt-1">SAR → IDR → USDT · USDT → SAR · SAR → USDT</p>
      </div>

      {!rate && (
        <Card className="p-4 border-amber-500/40 bg-amber-500/5 text-amber-300 text-sm">
          {t("no_rate_set")}
        </Card>
      )}

      <div className="flex gap-2">
        <button
          data-testid="mode-chain"
          onClick={() => { setMode("chain"); setSaved(null); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${mode === "chain" ? "bg-amber-500/10 text-amber-400 border-amber-500/40" : "text-slate-400 border-slate-800 hover:bg-slate-800/50"}`}
        >
          SAR → IDR → USDT
        </button>
        <button
          data-testid="mode-direct"
          onClick={() => { setMode("direct"); setSaved(null); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${mode === "direct" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/40" : "text-slate-400 border-slate-800 hover:bg-slate-800/50"}`}
        >
          USDT → SAR
        </button>
        <button
          data-testid="mode-direct-usdt"
          onClick={() => { setMode("direct_usdt"); setSaved(null); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${mode === "direct_usdt" ? "bg-violet-500/10 text-violet-400 border-violet-500/40" : "text-slate-400 border-slate-800 hover:bg-slate-800/50"}`}
        >
          SAR → USDT
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 glass border-slate-800 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>{t("customer")}</Label>
              <Select value={customerId} onValueChange={(v) => {
                setCustomerId(v);
                if (v === "walkin") setCustomerName("Walk-in");
                else {
                  const c = customers.find((x) => x.id === v);
                  setCustomerName(c?.name || "");
                }
              }}>
                <SelectTrigger data-testid="customer-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="walkin">Walk-in</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {mode === "chain" ? (
              <div>
                <Label>{t("from_amount")} — SAR</Label>
                <Input
                  data-testid="sar-input"
                  type="number" step="0.01"
                  value={sar}
                  onChange={(e) => setSar(e.target.value)}
                  placeholder="0.00"
                  className="mt-1.5 font-mono text-lg"
                />
              </div>
            ) : mode === "direct_usdt" ? (
              <div>
                <Label>{t("from_amount")} — SAR</Label>
                <Input
                  data-testid="sar-input-direct"
                  type="number" step="0.01"
                  value={sarIn4}
                  onChange={(e) => setSarIn4(e.target.value)}
                  placeholder="0.00"
                  className="mt-1.5 font-mono text-lg"
                />
              </div>
            ) : (
              <div>
                <Label>{t("from_amount")} — USDT</Label>
                <Input
                  data-testid="usdt-input"
                  type="number" step="0.0001"
                  value={usdtIn}
                  onChange={(e) => setUsdtIn(e.target.value)}
                  placeholder="0.00"
                  className="mt-1.5 font-mono text-lg"
                />
              </div>
            )}
          </div>

          {mode === "chain" && (
            <>
              <div className="rounded-xl border border-slate-800 p-4 space-y-3 bg-slate-900/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="pill bg-amber-500/15 text-amber-400 border border-amber-500/30">SAR</span>
                    <ArrowRight className="w-4 h-4 text-slate-500" />
                    <span className="pill bg-rose-500/15 text-rose-400 border border-rose-500/30">IDR</span>
                    <span className="text-sm font-medium ml-2">{t("leg_1")}</span>
                  </div>
                  <label className="text-xs text-slate-400 flex items-center gap-2 cursor-pointer">
                    <input data-testid="override-leg1" type="checkbox" checked={override1} onChange={(e) => setOverride1(e.target.checked)} />
                    {t("override")}
                  </label>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">{t("rate")} (IDR / SAR)</Label>
                    <Input
                      data-testid="leg1-rate"
                      type="number" step="0.0001"
                      value={leg1Rate}
                      onChange={(e) => setLeg1Rate(e.target.value)}
                      disabled={!override1 && !!rate}
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">IDR ({t("to_amount")})</Label>
                    <div data-testid="leg1-idr" className="mt-1 h-10 px-3 flex items-center rounded-md border border-slate-800 bg-slate-900 font-mono text-lg text-emerald-400">
                      {fmt(idr, 0)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t("margin")}</Label>
                    <div data-testid="leg1-margin" className={`mt-1 h-10 px-3 flex items-center rounded-md border border-slate-800 bg-slate-900 font-mono ${leg1Margin >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {fmt(leg1Margin, 0)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input data-testid="chain-toggle" id="chain" type="checkbox" checked={convertToUsdt} onChange={(e) => setConvertToUsdt(e.target.checked)} />
                <label htmlFor="chain" className="text-sm text-slate-300 cursor-pointer">Convert IDR to USDT (chain)</label>
              </div>

              {convertToUsdt && (
                <div className="rounded-xl border border-slate-800 p-4 space-y-3 bg-slate-900/40">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="pill bg-rose-500/15 text-rose-400 border border-rose-500/30">IDR</span>
                      <ArrowRight className="w-4 h-4 text-slate-500" />
                      <span className="pill bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">USDT</span>
                      <span className="text-sm font-medium ml-2">{t("leg_2")}</span>
                    </div>
                    <label className="text-xs text-slate-400 flex items-center gap-2 cursor-pointer">
                      <input data-testid="override-leg2" type="checkbox" checked={override2} onChange={(e) => setOverride2(e.target.checked)} />
                      {t("override")}
                    </label>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">{t("rate")} (IDR / USDT)</Label>
                      <Input
                        data-testid="leg2-rate"
                        type="number" step="0.01"
                        value={leg2Rate}
                        onChange={(e) => setLeg2Rate(e.target.value)}
                        disabled={!override2 && !!rate}
                        className="mt-1 font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("net_payout")}</Label>
                      <div data-testid="usdt-out" className="mt-1 h-10 px-3 flex items-center rounded-md border border-emerald-500/40 bg-emerald-500/5 font-mono text-lg text-emerald-400">
                        {fmt(usdt, 4)} USDT
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">{t("margin")}</Label>
                      <div data-testid="leg2-margin" className={`mt-1 h-10 px-3 flex items-center rounded-md border border-slate-800 bg-slate-900 font-mono ${leg2Margin >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {fmt(leg2Margin, 0)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {mode === "direct" && (
            <div className="rounded-xl border border-slate-800 p-4 space-y-3 bg-slate-900/40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="pill bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">USDT</span>
                  <ArrowRight className="w-4 h-4 text-slate-500" />
                  <span className="pill bg-amber-500/15 text-amber-400 border border-amber-500/30">SAR</span>
                  <span className="text-sm font-medium ml-2">USDT → SAR</span>
                </div>
                <label className="text-xs text-slate-400 flex items-center gap-2 cursor-pointer">
                  <input data-testid="override-leg3" type="checkbox" checked={override3} onChange={(e) => setOverride3(e.target.checked)} />
                  {t("override")}
                </label>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">{t("rate")} (SAR / USDT)</Label>
                  <Input
                    data-testid="leg3-rate"
                    type="number" step="0.0001"
                    value={leg3Rate}
                    onChange={(e) => setLeg3Rate(e.target.value)}
                    disabled={!override3 && !!rate}
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs">SAR ({t("to_amount")})</Label>
                  <div data-testid="sar-out" className="mt-1 h-10 px-3 flex items-center rounded-md border border-amber-500/40 bg-amber-500/5 font-mono text-lg text-amber-400">
                    {fmt(sarOut, 2)} SAR
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t("margin")} (IDR)</Label>
                  <div data-testid="leg3-margin" className={`mt-1 h-10 px-3 flex items-center rounded-md border border-slate-800 bg-slate-900 font-mono ${leg3Margin >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {fmt(leg3Margin, 0)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {mode === "direct_usdt" && (
            <div className="rounded-xl border border-slate-800 p-4 space-y-3 bg-slate-900/40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="pill bg-amber-500/15 text-amber-400 border border-amber-500/30">SAR</span>
                  <ArrowRight className="w-4 h-4 text-slate-500" />
                  <span className="pill bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">USDT</span>
                  <span className="text-sm font-medium ml-2">SAR → USDT</span>
                </div>
                <label className="text-xs text-slate-400 flex items-center gap-2 cursor-pointer">
                  <input data-testid="override-leg4" type="checkbox" checked={override4} onChange={(e) => setOverride4(e.target.checked)} />
                  {t("override")}
                </label>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">{t("rate")} (USDT / SAR)</Label>
                  <Input
                    data-testid="leg4-rate"
                    type="number" step="0.000001"
                    value={leg4Rate}
                    onChange={(e) => setLeg4Rate(e.target.value)}
                    disabled={!override4 && !!rate}
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs">USDT ({t("to_amount")})</Label>
                  <div data-testid="usdt-out-direct" className="mt-1 h-10 px-3 flex items-center rounded-md border border-emerald-500/40 bg-emerald-500/5 font-mono text-lg text-emerald-400">
                    {fmt(usdtOut4, 4)} USDT
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t("margin")} (IDR)</Label>
                  <div data-testid="leg4-margin" className={`mt-1 h-10 px-3 flex items-center rounded-md border border-slate-800 bg-slate-900 font-mono ${leg4Margin >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {fmt(leg4Margin, 0)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label>{t("notes")}</Label>
            <Textarea data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" rows={2} />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button data-testid="submit-tx" onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
              {t("submit_tx")}
            </Button>
            {saved && (
              <>
                <Button data-testid="print-btn" onClick={() => window.print()} variant="outline" className="border-slate-700">
                  <Printer className="w-4 h-4 mr-2" /> {t("print_receipt")}
                </Button>
                <Button onClick={reset} variant="ghost">New</Button>
              </>
            )}
            <Button onClick={loadData} variant="ghost" size="sm" className="ml-auto text-slate-400">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </Card>

        <Card className="p-6 glass border-slate-800 h-fit sticky top-20 space-y-4">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">{t("total_profit")}</div>
            <div data-testid="total-profit" className={`font-mono text-3xl font-bold mt-1 ${totalProfit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {fmt(totalProfit, 0)}
            </div>
          </div>
          <div className="pt-4 border-t border-slate-800 space-y-2 text-sm">
            {mode === "chain" ? (
              <>
                <div className="flex justify-between text-slate-400">
                  <span>SAR in</span>
                  <span className="font-mono text-slate-200">{fmt(sar || 0, 2)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>IDR</span>
                  <span className="font-mono text-slate-200">{fmt(idr, 0)}</span>
                </div>
                {convertToUsdt && (
                  <div className="flex justify-between text-slate-400">
                    <span>USDT out</span>
                    <span className="font-mono text-emerald-400">{fmt(usdt, 4)}</span>
                  </div>
                )}
              </>
            ) : mode === "direct_usdt" ? (
              <>
                <div className="flex justify-between text-slate-400">
                  <span>SAR in</span>
                  <span className="font-mono text-slate-200">{fmt(sarIn4 || 0, 2)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>USDT out</span>
                  <span className="font-mono text-emerald-400">{fmt(usdtOut4, 4)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between text-slate-400">
                  <span>USDT in</span>
                  <span className="font-mono text-slate-200">{fmt(usdtIn || 0, 4)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>SAR out</span>
                  <span className="font-mono text-amber-400">{fmt(sarOut, 2)}</span>
                </div>
              </>
            )}
          </div>
          {saved && (
            <div className="pt-4 border-t border-slate-800 text-xs text-slate-500">
              Saved as <span className="font-mono text-amber-400">{saved.tx_number}</span>
            </div>
          )}
        </Card>
      </div>

      {saved && <Receipt tx={saved} />}
    </div>
  );
}
